import { useCallback, useEffect, useState } from "react";
import { Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { api } from "../App";
import { Button } from "../components/ui/button";
import PageHeader from "../components/PageHeader";
import HistoryTable from "./history/HistoryTable";
import HistoryEmpty from "./history/HistoryEmpty";

export default function HistoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/history?limit=200");
      setItems(res.data || []);
    } catch {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const deleteOne = async (id) => {
    try {
      await api.delete(`/history/${id}`);
      setItems((list) => list.filter((x) => x.id !== id));
    } catch {
      toast.error("Failed to delete entry");
    }
  };

  const clearAll = async () => {
    if (!items.length) return;
    if (!window.confirm("Delete all history?")) return;
    try {
      const res = await api.delete("/history");
      toast.success(`Cleared ${res.data.deleted} entries`);
      setItems([]);
    } catch {
      toast.error("Failed to clear history");
    }
  };

  const renderBody = () => {
    if (loading && items.length === 0) {
      return <div className="text-sm text-[#5B695E]">Loading…</div>;
    }
    if (items.length === 0) {
      return <HistoryEmpty />;
    }
    return <HistoryTable items={items} onDelete={deleteOne} />;
  };

  return (
    <div className="p-6 sm:p-8 md:p-12 max-w-7xl mx-auto w-full">
      <PageHeader
        eyebrow="Audit"
        title="Lookup history"
        subtitle="Every lookup you run is logged locally in your MongoDB so you can revisit it later."
      >
        <Button
          onClick={load}
          variant="outline"
          className="border-border/60 hover:bg-[#F4F5F4]"
          data-testid="history-refresh-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Reload
        </Button>
        <Button
          onClick={clearAll}
          variant="outline"
          disabled={!items.length}
          data-testid="history-clear-btn"
          className="border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/5"
        >
          <Trash2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Clear all
        </Button>
      </PageHeader>

      {renderBody()}
    </div>
  );
}
